import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  createStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import EnumUtils from "utils/EnumUtils";
import { PlanType } from "types/enum/PlanType";
import { makeStyles } from "@material-ui/core/styles";
import useModal from "hooks/useModal";
import Modal from "components/Modal";
import PaymentApi from "api/PaymentApi";
import { Alert, AlertTitle } from "@material-ui/lab";
import { updatePlan } from "redux/actions/account";
import StaffRole from "types/enum/StaffRole";
import { useHistory } from "react-router-dom";
import RequestUtils from "utils/RequestUtils";

const Payment = () => {
  const [clientSecret, setClientSecret] = useState("");
  const history = useHistory();
  const staff = useSelector((state) => state.account?.staff);
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const [cardPaymentModal, setCardPaymentModal] = useModal("cardPaymentModal");
  // ステータス再取得トリガ。cancel / uncancel / card登録 後に bump する。
  const [statusVersion, setStatusVersion] = useState(0);
  const bumpStatus = () => setStatusVersion((v) => v + 1);

  useEffect(() => {
    if (!clientSecret) return;
    setCardPaymentModal(true);
  }, [clientSecret]);

  if (
    ![StaffRole.notelMaster, StaffRole.clientMaster].includes(
      EnumUtils.mapToEnum(StaffRole, staff?.role) ||
        StaffRole.clientShopPartTime
    )
  ) {
    /**
     * ★権限の 無い 方を TOP へ 戻すと 無限に 回る★（2026-08-13 実測）
     *
     *   止まっている → TOP の API が 402 → お支払い画面へ 送る
     *   → お支払い画面は 権限が 無いので TOP へ 戻す → また 402 → …
     *
     * 単体では どちらも 正しい 二つの 遷移が 噛み合って 回る。
     * ⇒ 停止で 送られて 来た 時だけは 戻さず、事情を 出して 止める。
     */
    if (RequestUtils.wasSentToPaymentBySuspension()) {
      return (
        <Typography style={{ margin: 24 }}>
          お支払いが確認できないため、ご利用を一時停止しております。
          <br />
          お支払いのお手続きは、貴社の管理者権限をお持ちの方にご依頼ください。
        </Typography>
      );
    }
    history.push("/");
    return null;
  }
  return (
    <>
      <SuspendedBanner statusVersion={statusVersion} />
      <CancellationBanner
        statusVersion={statusVersion}
        onChange={bumpStatus}
      />
      <TrialCardRegistrationCta
        statusVersion={statusVersion}
        onChange={bumpStatus}
      />
      <PaymentForm
        setClientSecret={setClientSecret}
        statusVersion={statusVersion}
        onChange={bumpStatus}
      />
      {clientSecret && (
        <Modal
          show={cardPaymentModal.show}
          setShow={setCardPaymentModal}
          title={"カード支払い情報登録"}
          disableEnforceFocus
        >
          <Typography style={{ margin: "20px 0" }}>
            今月の日割り料金：{cardPaymentModal?.meta?.price}円
          </Typography>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm clientSecret={clientSecret} />
          </Elements>
        </Modal>
      )}
    </>
  );
};

type PaymentFormProps = {
  setClientSecret: any;
  statusVersion: number;
  onChange: () => void;
};
const PaymentForm = ({
  setClientSecret,
  statusVersion,
  onChange,
}: PaymentFormProps) => {
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const bodyList = [
    { title: "月額料金", light: "5,000円", standard: "15,000円", premium: "30,000円", enterprise: "50,000円〜" },
    { title: "年額料金", light: "—", standard: "150,000円", premium: "300,000円", enterprise: "600,000円〜" },
    { title: "グループ機能", light: "×", standard: "○", premium: "○", enterprise: "○" },
    { title: "受注履歴保存期間", light: "1年", standard: "3年", premium: "5年", enterprise: "無制限" },
    { title: "店舗管理", light: "2件", standard: "10件", premium: "25件", enterprise: "無制限" },
    { title: "CTI", light: "○", standard: "○", premium: "○", enterprise: "○" },
    { title: "会社管理", light: "1社", standard: "3社", premium: "5社", enterprise: "無制限" },
    { title: "スタッフ管理", light: "2名", standard: "3名", premium: "10名", enterprise: "無制限" },
    { title: "掲示板", light: "○", standard: "○", premium: "○", enterprise: "○" },
    { title: "AIサポート", light: "○", standard: "○", premium: "○", enterprise: "○" },
    { title: "お知らせ管理", light: "5件", standard: "10件", premium: "25件", enterprise: "無制限" },
    { title: "顧客管理", light: "5000件", standard: "15000件", premium: "無制限", enterprise: "無制限" },
    { title: "ポイント管理", light: "5000件", standard: "15000件", premium: "無制限", enterprise: "無制限" },
    { title: "キャスト管理", light: "20名", standard: "40名", premium: "無制限", enterprise: "無制限" },
    { title: "源氏名管理", light: "30件", standard: "80件", premium: "無制限", enterprise: "無制限" },
    { title: "キャスト区分管理", light: "8件", standard: "無制限", premium: "無制限", enterprise: "無制限" },
    { title: "指名種管理", light: "6件", standard: "無制限", premium: "無制限", enterprise: "無制限" },
    { title: "クラス管理", light: "6件", standard: "15件", premium: "無制限", enterprise: "無制限" },
    { title: "ドライバー管理", light: "5名", standard: "15名", premium: "無制限", enterprise: "無制限" },
    { title: "オプション管理", light: "25件", standard: "無制限", premium: "無制限", enterprise: "無制限" },
    { title: "イベント・割引管理", light: "5件", standard: "10件", premium: "無制限", enterprise: "無制限" },
    { title: "媒体管理", light: "10件", standard: "20件", premium: "無制限", enterprise: "無制限" },
    { title: "エリア・ホテル管理", light: "無制限", standard: "無制限", premium: "無制限", enterprise: "無制限" },
    { title: "コスプレ管理", light: "10件", standard: "20件", premium: "無制限", enterprise: "無制限" },
    { title: "コース管理", light: "無制限", standard: "無制限", premium: "無制限", enterprise: "無制限" },
    { title: "延長管理", light: "無制限", standard: "無制限", premium: "無制限", enterprise: "無制限" },
    { title: "顧客区分管理", light: "3件", standard: "5件", premium: "無制限", enterprise: "無制限" },
    { title: "予約メール管理", light: "○", standard: "○", premium: "○", enterprise: "○" },
    { title: "チャットサポート", light: "無料", standard: "無料", premium: "無料", enterprise: "無料" },
    { title: "オンラインサポート", light: "無料", standard: "無料", premium: "無料", enterprise: "無料" },
    { title: "電話サポート", light: "無料", standard: "無料", premium: "無料", enterprise: "無料" },
    { title: "訪問サポート", light: "×", standard: "○", premium: "○", enterprise: "○" },
    { title: "データインポート代行", light: "1件あたり5円", standard: "1件あたり3円", premium: "無制限", enterprise: "無制限" },
  ];
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
      cell: {
        position: "sticky",
      },
    })
  );
  const classes = useStyles();
  const [, setPlanSelectModal] = useModal("planSelectModal");

  return (
    <Paper style={{ margin: "40px" }}>
      {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise && (
        <Alert>
          <AlertTitle>
            現在エンタープライズをご契約のためプラン変更を行いたい場合は、お問い合わせください。
          </AlertTitle>
        </Alert>
      )}
      <Alert color={"info"}>
        <AlertTitle>
          以下のドメインよりメールを送付するため、ドメイン解除設定をお願いいたします。
        </AlertTitle>
        <Box display={"flex"} flexDirection={"column"}>
          <Typography>
            またメールが届かない方は、翌日以降画面下部に請求書URLが表示されますので、そちらからご決済をお願いいたします。
          </Typography>
          <Typography>notel.com</Typography>
          <Typography>stripe.com</Typography>
        </Box>
      </Alert>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>ライト</TableCell>
            <TableCell>スタンダード</TableCell>
            <TableCell>プレミアム</TableCell>
            <TableCell>エンタープライズ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell />
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "light" })}
                disabled={[PlanType.light, PlanType.enterprise].includes(
                  EnumUtils.mapToEnum(PlanType, currentPlan) || PlanType.free
                )}
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.light
                  ? "現在のプラン"
                  : "このプランにする"}
              </Button>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "starter" })}
                disabled={[PlanType.starter, PlanType.enterprise].includes(
                  EnumUtils.mapToEnum(PlanType, currentPlan) || PlanType.free
                )}
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.starter
                  ? "現在のプラン"
                  : "このプランにする"}
              </Button>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "premium" })}
                disabled={[PlanType.premium, PlanType.enterprise].includes(
                  EnumUtils.mapToEnum(PlanType, currentPlan) || PlanType.free
                )}
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.premium
                  ? "現在のプラン"
                  : "このプランにする"}
              </Button>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "enterprise" })}
                disabled={
                  EnumUtils.mapToEnum(PlanType, currentPlan) ===
                  PlanType.enterprise
                }
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) ===
                PlanType.enterprise
                  ? "現在のプラン"
                  : "問い合わせする"}
              </Button>
            </TableCell>
          </TableRow>
          {bodyList.map((body, index) => (
            <TableRow key={index}>
              <TableCell>{body.title}</TableCell>
              <TableCell>{body.light}</TableCell>
              <TableCell>{body.standard}</TableCell>
              <TableCell>{body.premium}</TableCell>
              <TableCell>{body.enterprise}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <CancelPlanSection
        statusVersion={statusVersion}
        onChange={onChange}
      />
      <PlanSelectModal setClientSecret={setClientSecret} />
      <PaymentSendMailModal />
    </Paper>
  );
};

type CancelSectionProps = { statusVersion: number; onChange: () => void };
const CancelPlanSection = ({
  statusVersion,
  onChange,
}: CancelSectionProps) => {
  const companyId = useSelector((state) => state.account.staff.companyId);
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [willEnd, setWillEnd] = useState<boolean | null>(null);

  useEffect(() => {
    if (
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.free ||
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise
    ) {
      return;
    }
    (async () => {
      try {
        const res = await PaymentApi.getStatus(companyId);
        setWillEnd(res.willEnd);
      } catch (_e) {
        /* noop */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentPlan, statusVersion]);

  // 停止状態 (free) or enterprise は解約ボタン出さない
  if (
    EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.free ||
    EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise
  ) {
    return null;
  }
  // 解約予定中は上部のバナーで状態表示してるので、ここの解約ボタンは隠す
  if (willEnd) return null;

  const reset = () => {
    setOpen(false);
    setStep(1);
    setAck(false);
    setError("");
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await PaymentApi.cancelSubscription(companyId);
      setOpen(false);
      setWillEnd(true);
      onChange();
    } catch (e: any) {
      setError(e?.message || "解約処理に失敗しました。再度お試しください。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mx={5} my={4}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          color="default"
          onClick={() => setOpen(true)}
          style={{ color: "#b00020", borderColor: "#b00020" }}
        >
          プランを解約する
        </Button>
      </Box>
      <Modal show={open} setShow={(v: boolean) => (v ? setOpen(true) : reset())}>
        <Box display="flex" flexDirection="column" style={{ gap: 16, padding: 4 }}>
          {step === 1 && (
            <>
              <Typography variant="h6" style={{ color: "#b00020" }}>
                プラン解約のご確認 (1/3)
              </Typography>
              <Alert severity="warning">
                <AlertTitle>重要: 解約するとサービスが停止します</AlertTitle>
                解約後は、現在のご契約期間終了時にアカウントが停止され、
                以下へのアクセスができなくなります:
                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                  <li>スタッフ・キャスト・ドライバーの管理画面</li>
                  <li>顧客データ・受注履歴</li>
                  <li>モバイルアプリ (キャスト/ドライバー/顧客)</li>
                </ul>
              </Alert>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button variant="outlined" onClick={reset}>
                  キャンセル
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#b00020", color: "#fff" }}
                  onClick={() => setStep(2)}
                >
                  次へ
                </Button>
              </Box>
            </>
          )}
          {step === 2 && (
            <>
              <Typography variant="h6" style={{ color: "#b00020" }}>
                プラン解約のご確認 (2/3)
              </Typography>
              <Alert severity="warning">
                <AlertTitle>重要: データの復元は保証できません</AlertTitle>
                アカウント停止後のデータ復元 (再契約等) は、保存期間や復元可否について
                保証いたしかねます。重要なデータは事前に CSV エクスポート等で
                お手元にお控えください。
              </Alert>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button variant="outlined" onClick={() => setStep(1)}>
                  戻る
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#b00020", color: "#fff" }}
                  onClick={() => setStep(3)}
                >
                  次へ
                </Button>
              </Box>
            </>
          )}
          {step === 3 && (
            <>
              <Typography variant="h6" style={{ color: "#b00020" }}>
                プラン解約の最終確認 (3/3)
              </Typography>
              <Alert severity="error">
                <AlertTitle>最終確認</AlertTitle>
                上記を全てご確認のうえ、本当に解約する場合は下記にチェックを入れて
                「解約を確定する」を押してください。
              </Alert>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                />
                <Typography variant="body2">
                  解約による影響を理解しました
                </Typography>
              </Box>
              {error && <Alert severity="error">{error}</Alert>}
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  disabled={loading}
                  onClick={() => setStep(2)}
                >
                  戻る
                </Button>
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: ack && !loading ? "#b00020" : "#ccc",
                    color: "#fff",
                  }}
                  disabled={!ack || loading}
                  onClick={submit}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {loading ? "処理中..." : "解約を確定する"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

type CheckoutFormProps = {
  clientSecret: string;
};
const CheckoutForm = ({ clientSecret }: CheckoutFormProps) => {
  const [cardPaymentModal, setCardPaymentModal] = useModal("cardPaymentModal");
  const companyGroup = useSelector(
    (state) => state.account.staff.company.companyGroup
  );
  const companyId = useSelector((state) => state.account.staff.companyId);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
      cell: {
        position: "sticky",
      },
    })
  );
  const classes = useStyles();
  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (loading) return; // 多重送信防止
    setError("");

    if (!stripe || !elements) {
      setError("Stripe.js の読み込み中です。少々お待ちください。");
      return;
    }

    setLoading(true);
    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement) || { token: "" },
        },
      });
      if (result.error) {
        setError(
          result.error.message ||
            "カード決済に失敗しました。カード情報をご確認ください。"
        );
        return;
      }
      if ((result as any).paymentIntent?.status === "succeeded") {
        await PaymentApi.updatePaymentMethod(
          companyId,
          (result as any).paymentIntent.payment_method || ""
        );
        dispatch(
          updatePlan(
            {
              ...companyGroup,
              plan: cardPaymentModal.meta?.plan,
            },
            companyId
          )
        );
        setCardPaymentModal(false, {});
      } else {
        setError(
          `決済ステータスが予期せぬ状態です: ${
            (result as any).paymentIntent?.status
          }`
        );
      }
    } catch (e) {
      setError(
        (e as Error)?.message ||
          "エラーが発生しました。ネットワークをご確認の上、再度お試しください。"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardElement options={{ hidePostalCode: true }} />
      {error && (
        <Alert severity="error" style={{ marginTop: 12 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginTop={2}
          style={{ gap: 8 }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            決済処理中です。画面を閉じずにお待ちください...
          </Typography>
        </Box>
      )}
      <Box display="flex" justifyContent="center" margin={2}>
        <Button
          disabled={!stripe || loading}
          onClick={handleSubmit}
          variant="contained"
          className={classes.button}
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? "処理中..." : "送信"}
        </Button>
      </Box>
    </>
  );
};

const PlanSelectModal = ({ setClientSecret }: { setClientSecret: any }) => {
  const [planSelectModal, setPlanSelectModal] = useModal("planSelectModal");
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [, setCardPaymentModal] = useModal("cardPaymentModal");
  const [, setPaymentSendMailModal] = useModal("paymentSendMailModal");
  const companyGroup = useSelector(
    (state) => state.account.staff.company.companyGroup
  );
  const dispatch = useDispatch();
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
      cell: {
        position: "sticky",
      },
    })
  );
  const classes = useStyles();
  const planPrice: any = {
    light: {
      month: {
        value: "5,000円",
        id: process.env.REACT_APP_LIGHT_MONTH_PRICE_ID,
      },
      // ライトプランは現状月払いのみ (年払い未定)
    },
    starter: {
      month: {
        value: "15,000円",
        id: process.env.REACT_APP_STANDARD_MONTH_PRICE_ID,
      },
      year: {
        value: "150,000円",
        id: process.env.REACT_APP_STANDARD_YEAR_PRICE_ID,
      },
    },
    premium: {
      month: {
        value: "30,000円",
        id: process.env.REACT_APP_PREMIUM_MONTH_PRICE_ID,
      },
      year: {
        value: "300,000円",
        id: process.env.REACT_APP_PREMIUM_YEAR_PRICE_ID,
      },
    },
  };

  const clickAtCardPayment = async (planId: string, isAnnual: boolean) => {
    const customer = await PaymentApi.findOrCreateSubscription(
      companyId,
      planId,
      isAnnual
    );
    setClientSecret(customer.clientSecret);
    if (!customer.clientSecret) {
      dispatch(
        updatePlan(
          {
            ...companyGroup,
            plan: planSelectModal.meta?.plan,
          },
          companyId
        )
      );
    }
    setPlanSelectModal(false, {});
    setCardPaymentModal(true, {
      price: customer.price,
      plan: planSelectModal.meta?.plan,
    });
  };

  const clickAtPayment = async (
    planId: string,
    isAnnual: boolean,
    planType: PlanType
  ) => {
    await PaymentApi.findOrCreateSubscriptionOnMail(
      companyId,
      planId,
      isAnnual,
      planType
    );
    setPlanSelectModal(false, {});
    setPaymentSendMailModal(true);
  };

  return (
    <Modal
      show={planSelectModal.show}
      setShow={setPlanSelectModal}
      title={`${EnumUtils.mapToEnum(
        PlanType,
        planSelectModal.meta?.plan || ""
      )}への切り替え`}
    >
      {EnumUtils.mapToEnum(PlanType, planSelectModal.meta?.plan || "") ===
      PlanType.enterprise ? (
        <Box margin={1}>
          <Typography>右下のチャット欄よりお問い合わせください。</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>
              月額
              {planPrice[planSelectModal.meta?.plan || ""]?.month.value}
            </Typography>
            <Box display={"flex"}>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() =>
                  clickAtCardPayment(
                    String(
                      planPrice[planSelectModal.meta?.plan || ""]?.month.id
                    ),
                    false
                  )
                }
              >
                カード支払いへ
              </Button>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() =>
                  clickAtPayment(
                    String(
                      planPrice[planSelectModal.meta?.plan || ""]?.month.id
                    ),
                    false,
                    planSelectModal.meta?.plan
                  )
                }
              >
                コンビニ支払いへ
              </Button>
            </Box>
          </Box>
          {planPrice[planSelectModal.meta?.plan || ""]?.year && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>
                年額{planPrice[planSelectModal.meta?.plan || ""]?.year.value}
              </Typography>
              <Box display={"flex"}>
                <Button
                  variant="contained"
                  className={classes.button}
                  color="primary"
                  onClick={() =>
                    clickAtCardPayment(
                      String(
                        planPrice[planSelectModal.meta?.plan || ""]?.year.id
                      ),
                      true
                    )
                  }
                >
                  カード支払いへ
                </Button>
                <Button
                  variant="contained"
                  className={classes.button}
                  color="primary"
                  onClick={() =>
                    clickAtPayment(
                      String(
                        planPrice[planSelectModal.meta?.plan || ""]?.year.id
                      ),
                      true,
                      planSelectModal.meta?.plan
                    )
                  }
                >
                  コンビニ支払いへ
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Modal>
  );
};

const PaymentSendMailModal = () => {
  const [paymentSendMailModal, setPaymentSendMailModal] = useModal(
    "paymentSendMailModal"
  );
  const email = useSelector((state) => state.account.staff.company.email);
  return (
    <Modal show={paymentSendMailModal.show} setShow={setPaymentSendMailModal}>
      <Box>
        <Typography>{email}宛にメールを送信しました。</Typography>
      </Box>
    </Modal>
  );
};

/**
 * トライアル中のライトプラン会社向けカード登録 CTA。
 * Payment 画面トップに常設し、Stripe SetupIntent でカード保存 → updatePaymentMethod でデフォルト化。
 */
type CtaProps = { statusVersion: number; onChange: () => void };

/**
 * ★未払いで 止まっている間の 帯★（2026-08-13・代表 判断 甲）
 *
 * この画面の 既存の 3 つの 手（プラン選択 / カード登録 / 無料へ 変更）は
 * ★どれも「既に 立っている 未払いの 請求書」を 払う 手では 無い★。
 * ⇒ サーバが 返す 請求書の 頁（Stripe・ログイン不要）へ 直に 行けるようにする。
 *
 * 止まっていない 時は 何も 出さない（return null）ので、平時の 画面は 変わらない。
 */
const SuspendedBanner = ({ statusVersion }: { statusVersion: number }) => {
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [suspended, setSuspended] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await PaymentApi.getStatus(companyId);
        setSuspended(!!res.suspended);
        setUrl(res.hostedInvoiceUrl ?? null);
        setAmount(res.amountDueJpy ?? null);
      } catch (_e) {
        /* 取れない時は 出さない。誤って 止まっている顔を 見せない。 */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, statusVersion]);

  if (!suspended) return null;

  return (
    <Alert severity="error" style={{ margin: "16px 0" }}>
      <AlertTitle>お支払いが確認できないため、ご利用を一時停止しております</AlertTitle>
      {amount !== null && (
        <Typography style={{ marginBottom: 8 }}>
          お支払い残高：{amount.toLocaleString()}円
        </Typography>
      )}
      {url ? (
        <Button
          variant="contained"
          color="primary"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          お支払いへ
        </Button>
      ) : (
        <Typography>
          お手数ですが、サポートまでお問い合わせください。
        </Typography>
      )}
    </Alert>
  );
};

/**
 * 解約予定の赤バナー。willEnd=true のとき上部に表示、解約取消ボタン付き。
 */
const CancellationBanner = ({ statusVersion, onChange }: CtaProps) => {
  const companyId = useSelector((state) => state.account.staff.companyId);
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const [willEnd, setWillEnd] = useState<boolean | null>(null);
  const [cancelAt, setCancelAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const res = await PaymentApi.getStatus(companyId);
      setWillEnd(res.willEnd);
      setCancelAt(res.cancelAt);
    } catch (_e) {
      /* noop */
    }
  };

  useEffect(() => {
    if (
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.free ||
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise
    )
      return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentPlan, statusVersion]);

  if (!willEnd) return null;

  const onUncancel = async () => {
    setLoading(true);
    setError("");
    try {
      await PaymentApi.uncancelSubscription(companyId);
      setWillEnd(false);
      onChange();
    } catch (e: any) {
      setError(e?.message || "解約取消に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (iso: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <Box mx={5} mt={5} mb={2}>
      <Alert
        severity="error"
        action={
          <Button
            color="primary"
            variant="contained"
            disabled={loading}
            onClick={onUncancel}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {loading ? "処理中..." : "解約を取り消す"}
          </Button>
        }
      >
        <AlertTitle>
          このプランは {fmt(cancelAt) || "期間末"} に解約されます
        </AlertTitle>
        解約予定日までは引き続きご利用いただけます。継続ご希望の場合は「解約を取り消す」をご利用ください。
      </Alert>
      {error && (
        <Alert severity="error" style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

const TrialCardRegistrationCta = ({ statusVersion, onChange }: CtaProps) => {
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const companyId = useSelector((state) => state.account.staff.companyId);
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const [modal, setModal] = useModal("cardRegistrationModal");
  const [clientSecret, setClientSecret] = useState("");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(
    null
  );
  const [justRegistered, setJustRegistered] = useState(false);

  const refresh = async () => {
    try {
      const res = await PaymentApi.getStatus(companyId);
      setHasPaymentMethod(res.hasPaymentMethod);
    } catch (_e) {
      // 失敗時は null のまま
    }
  };

  useEffect(() => {
    if (EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.light) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan, companyId, statusVersion]);

  if (EnumUtils.mapToEnum(PlanType, currentPlan) !== PlanType.light) {
    return null;
  }
  // ステータス取得前は表示保留 (一瞬の誤表示回避)
  if (hasPaymentMethod === null) return null;

  const open = async () => {
    setOpening(true);
    setError("");
    try {
      const res = await PaymentApi.createSetupIntent(companyId);
      setClientSecret(res.clientSecret);
      setModal(true);
    } catch (e: any) {
      setError(
        e?.message || "カード登録の準備に失敗しました。再度お試しください。"
      );
    } finally {
      setOpening(false);
    }
  };

  const onRegistered = () => {
    setModal(false);
    setClientSecret("");
    setHasPaymentMethod(true);
    setJustRegistered(true);
    refresh();
    onChange();
  };

  return (
    <Box mx={5} mt={5} mb={2}>
      {hasPaymentMethod ? (
        <Alert
          severity="success"
          action={
            <Button
              color="primary"
              variant="outlined"
              disabled={opening}
              onClick={open}
              startIcon={
                opening ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              カード情報を変更
            </Button>
          }
        >
          <AlertTitle>カード情報 登録済み</AlertTitle>
          {justRegistered
            ? "カード情報を登録しました。トライアル終了後に自動的に課金が開始されます。"
            : "トライアル終了後に自動的に課金が開始されます。"}
        </Alert>
      ) : (
        <Alert
          severity="info"
          action={
            <Button
              color="primary"
              variant="contained"
              disabled={opening}
              onClick={open}
              startIcon={
                opening ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              カード情報を登録
            </Button>
          }
        >
          <AlertTitle>トライアル期間中です</AlertTitle>
          引き続きご利用の場合は、カード情報のご登録をお願いいたします。期限を過ぎるとアカウントが一時停止となります。
        </Alert>
      )}
      {error && (
        <Alert severity="error" style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}
      {clientSecret && (
        <Modal
          show={modal.show}
          setShow={setModal}
          title={hasPaymentMethod ? "カード情報の変更" : "カード情報の登録"}
        >
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <SetupIntentForm
              clientSecret={clientSecret}
              onClose={() => {
                setModal(false);
                setClientSecret("");
              }}
              onSuccess={onRegistered}
            />
          </Elements>
        </Modal>
      )}
    </Box>
  );
};

type SetupIntentFormProps = {
  clientSecret: string;
  onClose: () => void;
  onSuccess?: () => void;
};
const SetupIntentForm = ({
  clientSecret,
  onClose,
  onSuccess,
}: SetupIntentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");
    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error("カード入力欄が見つかりません。");
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if ((result as any).error) {
        setError(
          (result as any).error?.message || "カードの確認に失敗しました。"
        );
        return;
      }
      const pmId = (result as any).setupIntent?.payment_method;
      if (!pmId) {
        setError("カード情報を取得できませんでした。");
        return;
      }
      await PaymentApi.updatePaymentMethod(companyId, pmId);
      if (onSuccess) onSuccess();
      else onClose();
    } catch (e: any) {
      setError(
        e?.message ||
          "ネットワークエラーが発生しました。再度お試しください。"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Typography variant="body2" color="textSecondary">
        カード情報を入力してください。トライアル期限を過ぎると自動的に課金が開始されます。
      </Typography>
      <Box p={2} border={1} borderColor="divider" borderRadius={4}>
        <CardElement options={{ hidePostalCode: true }} />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
        <Button variant="outlined" disabled={loading} onClick={onClose}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !stripe}
          onClick={submit}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {loading ? "登録中..." : "登録する"}
        </Button>
      </Box>
    </Box>
  );
};

export default Payment;
